// @ts-nocheck
import React, { useState, useEffect, useMemo, useRef, useId } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setModalOpen, setModalState, modalViewsConst } from "../../store/modalSlice";
import {
  fetchProcessedModelSchemaById,
  editProcessedModelSchemaFunc,
} from "../../store/processedModelSchemaSlice";
import {
  deployProcessedModelSchema,
  prepareSchemaFieldsForSave,
  buildProcessedModelDeployTarget,
  buildInitialSchemaFields,
  generateProcessedModelSchemaId,
  injectTypesToSchemaFields,
  ProcessedModelDeployError,
} from "../../store/processedModelDeploy";
import { fetchModelSchemaTemplates } from "../../store/modelSchemaTemplateSlice";
import { ReactComponent as LightningIcon } from "../../assets/icons/lightning.svg";
import { ReactComponent as ChevronRightIcon } from "../../assets/icons/chevron-right.svg";
import { FiPlus } from "react-icons/fi";
import { generateClient } from "aws-amplify/api";
import { processedModelSchedulesByProcessedModelSchemaIdAndCreatedAt } from "../../graphql/queries";
import ModalBase from "./ModalBase";
import SchemaFieldRenderer from "../SchemaFieldRenderer";
import ScheduleEditor from "../Schedule/ScheduleEditor";
import {
  defaultScheduleConfig,
  formatDatetimeLocal,
  scheduleConfigToRunSchedule,
  scheduleRowToScheduleConfig,
} from "../Schedule/scheduleConfig";
import { buildRuntime } from "../../utils/scheduleFrequency";
import { reconcileProcessedModelSchedules } from "../../utils/processedModelScheduleSync";
import { parseDeliveryExpectation } from "../../utils/deliveryExpectation";
import { deliveryFormFrom, expectationFromDeliveryForm, summarizeDeliveryForm } from "../../utils/deliveryExpectationForm";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { useProcessedModelLimit } from "../../hooks/useProcessedModelLimit";
import { validateFieldValue, MODEL_SCHEMAS } from "../../types/modelSchemas";
import { isOutputFieldKey, outputParameterTypeId } from "../../utils/modelOutputUtils";
import { destinationProblem } from "../../utils/outputDestination";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";
import { effectiveConfig, toIso } from "../../utils/historicalRuns";
import { allowedTriggersOf, describeHistoryPlan, historicalSettingsFromForm, rerunPromptFor, rerunPromptText, rulesErrors } from "../../utils/historicalModelRuns";
import { createRunRequest, getTargetDataset, listOutputs } from "../../services/historicalModelService";

import Select from "../ReusableComponents/Select";
const scheduleClient = generateClient();

const DynamicProcessedModelSchemaModal = () => {
  const modelTypeId = useId();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const modalState = useSelector((state) => state.modalState);
  const processedModelSchemaState = useSelector((state) => state.processedModelSchemas);
  const modelSchemaTemplateState = useSelector((state) => state.modelSchemaTemplates);
  const { evaluate: evaluateModelLimit, isAdmin } = useProcessedModelLimit();
  const { capture } = usePostHogCapture();

  // Form state
  const [currentStep, setCurrentStep] = useState(1);
  const [schemaId, setSchemaId] = useState("");
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "",
    typeId: "", // Store the selected template ID
    executionUrl: "", // For custom models or overriding default URLs
    schemaFields: {}, // Store the dynamic schema field values
    dataSources: [],
    runSchedule: {
      firstRunTime: "",
      frequency: "ONCE",
      cronExpression: "",
      timezone: "UTC"
    },
    schedules: [defaultScheduleConfig()], // ScheduleConfig[] — the authoritative schedules
    isEditMode: false,
    dependency: "", // Dependency for EventBridge pipeline (e.g., forecast source)
    isActive: true,
    ownerType: "personal", // "personal" or "organization"
    selectedOrgKey: "", // orgKey if organization is selected
    // HISTORICAL templates (docs/plans/HISTORICAL_PROCESSED_MODELS_PLAN.md, stage 4): the History step
    processHistory: true,
    backfillFrom: "", // YYYY-MM-DD (UTC), empty = from the dataset's first day
  });
  const [errors, setErrors] = useState({});
  // External templates: when forecasts are expected (graded on the Uploads tab's calendar), and the value as stored
  // when editing, so changing only the allowance keeps the grading start (docs/architecture/EXTERNAL_FORECASTS.md §6)
  const [delivery, setDelivery] = useState(() => deliveryFormFrom(null));
  const [storedDelivery, setStoredDelivery] = useState(null);
  // Edit of a HISTORICAL model: the settings as stored (to tell what changed), its data start, and the
  // "re-run history?" offer after a save (decision 7)
  const [editBefore, setEditBefore] = useState(null);
  const [editStoredHistorical, setEditStoredHistorical] = useState({});
  // The edited model's data start (the start of the dataset its output writes), read when the wizard opens; the save
  // awaits it for the re-run prompt
  const editDataStartRef = useRef(Promise.resolve(null));
  const [rerunPrompt, setRerunPrompt] = useState(null);
  const [rerunState, setRerunState] = useState("idle"); // idle | queuing | queued | declined | failed
  const [uploadStatus, setUploadStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [currentUploadFile, setCurrentUploadFile] = useState('');

  // Operations log state for tracking all submission operations
  const [operationsLog, setOperationsLog] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  // Set only by a successful create, so an error or an edit never triggers __navigateOnCreate
  const [createSucceeded, setCreateSucceeded] = useState(false);
  const navigateOnCreate = createSucceeded ? modalState.properties?.__navigateOnCreate : undefined;

  // Helper to add a new operation to the log
  const addOperation = (id, type, name, status = 'pending') => {
    const operation = {
      id,
      type, // 'record', 'file', 'output', 'asset_link', 'refetch'
      name,
      status, // 'pending', 'in_progress', 'success', 'error'
      error: null,
      timestamp: new Date().toISOString()
    };
    setOperationsLog(prev => [...prev, operation]);
    return operation;
  };

  // Helper to update an operation's status
  const updateOperation = (id, updates) => {
    setOperationsLog(prev => prev.map(op =>
      op.id === id ? { ...op, ...updates } : op
    ));
  };

  // Define which fields can contain file uploads const UPLOAD_FIELDS = ['bounds', 'flowGaugeData', 'precipitationData', 'powerCurve', 'inverterData'];

  // Helper function to get display path based on ownership selection
  const getDisplayPath = (schemaId = 'GENERATED_ON_SUBMIT') => {
    if (formData.ownerType === "organization" && formData.selectedOrgKey) {
      return `organizationprivate/${formData.selectedOrgKey}/processedModelSchema/${schemaId}/`;
    } else {
      return `private/${user?.permissionDetails?.cognitoIdentityId || 'USER_ID'}/processedModelSchema/${schemaId}/`;
    }
  };

  // Load model templates when modal opens
  useEffect(() => {
    loadModelTemplates();
  }, []);

  // Check if a template was passed from Model Garden
  useEffect(() => {
    if (modalState.properties?.selectedTemplate) {
      const template = modalState.properties.selectedTemplate;
      setSelectedTemplate(template);
      // Initialize field defaults (e.g. defaultChecked outputs) — this path previously skipped default init, leaving defaultChecked boxes unticked.
      let fieldDefinitions = {};
      try {
        fieldDefinitions = typeof template.fieldDefinitions === 'string'
          ? JSON.parse(template.fieldDefinitions)
          : (template.fieldDefinitions || resolveTemplate(template.id).fieldDefinitions);
      } catch {
        fieldDefinitions = resolveTemplate(template.id).fieldDefinitions;
      }
      setFormData(prev => ({
        ...prev,
        type: template.name || '',
        typeId: template.id || '',
        executionUrl: template.executionUrl || '',
        schemaFields: buildInitialSchemaFields(fieldDefinitions)
      }));
      setCurrentStep(1);
    }
  }, [modalState.properties?.selectedTemplate]);

  // Resolve a template (dynamic DB or static) WITHOUT mutating state — used by edit pre-fill.
  const resolveTemplate = (templateId) => {
    let template = modelSchemaTemplateState.templateList.find(t => t.id === templateId);
    let fieldDefinitions = {};
    if (template) {
      fieldDefinitions = template?.fieldDefinitions ? JSON.parse(template.fieldDefinitions) : {};
    } else {
      const staticSchema = MODEL_SCHEMAS[templateId];
      if (staticSchema) {
        template = {
          id: templateId,
          name: staticSchema.name,
          description: staticSchema.description,
          category: staticSchema.category,
          version: staticSchema.version,
          executionUrl: staticSchema.executionUrl,
        };
        fieldDefinitions = staticSchema.fields || {};
      }
    }
    return { template, fieldDefinitions };
  };

  // Edit mode: one-time pre-fill from the schema being edited (+ load its schedule rows).
  const editInitRef = useRef(false);
  useEffect(() => {
    const editing = modalState.properties?.schemaToEdit;
    if (!editing || editInitRef.current) return;
    editInitRef.current = true;

    let parsedFields = {};
    try { parsedFields = JSON.parse(editing.schemaFields || "{}"); } catch { parsedFields = {}; }
    let storedHistorical = {};
    try {
      storedHistorical = typeof editing.historicalSettings === "string" ? JSON.parse(editing.historicalSettings) : editing.historicalSettings || {};
    } catch { storedHistorical = {}; }
    setEditBefore(parsedFields);
    setEditStoredHistorical(storedHistorical);
    const storedExpectation = parseDeliveryExpectation(editing.deliveryExpectation);
    setStoredDelivery(storedExpectation);
    setDelivery(deliveryFormFrom(storedExpectation));
    if (editing.modelKind === "HISTORICAL") {
      // Kept as a promise too: a save before this answers waits for it, so the re-run prompt sees the real dataStart
      editDataStartRef.current = listOutputs(editing.id)
        .then(async (outputs) => {
          const target = outputs.find((o) => o.storageMode === "DATASET" && o.targetDatasetId);
          const dataset = target?.targetDatasetId ? await getTargetDataset(target.targetDatasetId) : null;
          return dataset?.timeRange?.startDate ?? null;
        })
        .catch(() => null);
    }

    setSchemaId(editing.id);
    setFormData(prev => ({
      ...prev,
      isEditMode: true,
      name: editing.name || "",
      description: editing.description || "",
      type: editing.type || "",
      typeId: editing.schemaType || "",
      executionUrl: editing.executionUrl || "",
      schemaFields: parsedFields,
      dependency: editing.dependency || "",
      isActive: editing.isActive ?? true,
      ownerType: editing.organizationId ? "organization" : "personal",
      selectedOrgKey: editing.organizationId || "",
      processHistory: storedHistorical.autoBackfill !== false,
      backfillFrom: typeof storedHistorical.backfillFrom === "string" ? storedHistorical.backfillFrom.slice(0, 10) : "",
    }));
    setCurrentStep(1);

    (async () => {
      try {
        const { data } = await scheduleClient.graphql({
          query: processedModelSchedulesByProcessedModelSchemaIdAndCreatedAt,
          variables: { processedModelSchemaId: editing.id },
        });
        const rows = data?.processedModelSchedulesByProcessedModelSchemaIdAndCreatedAt?.items || [];
        setFormData(prev => ({
          ...prev,
          schedules: rows.length ? rows.map(scheduleRowToScheduleConfig) : [defaultScheduleConfig()],
        }));
      } catch (e) {
        console.log("Failed to load schedules for edit", e?.message);
      }
    })();
  }, [modalState.properties?.schemaToEdit]);

  // Import mode: one-time pre-fill from an exported schema to create a NEW schema. Like edit pre-fill, but stays in create mode (isEditMode: false) so submit calls postProcessedModelSchema and a fresh schemaId is generated. Outputs are stored as separate records (stripped from schemaFields), so we re-tick them from `outputs`.
  const importInitRef = useRef(false);
  useEffect(() => {
    const imported = modalState.properties?.importedSchema;
    if (!imported || importInitRef.current) return;

    const typeId = imported.schemaType || "";
    const { template, fieldDefinitions } = resolveTemplate(typeId);
    // Wait for the (possibly late-loading) template before prefilling so outputs can be reconstructed from its field definitions.
    if (typeId && !template && modelSchemaTemplateState.loading === 'pending') return;
    importInitRef.current = true;

    // The stored config inputs — outputs are NOT in here, they live in `outputs`.
    let importedFields = {};
    try { importedFields = JSON.parse(imported.schemaFields || "{}"); } catch { importedFields = {}; }

    // Rebuild schemaFields: template defaults < imported config inputs.
    const schemaFields = { ...buildInitialSchemaFields(fieldDefinitions), ...importedFields };

    // Re-tick output fields that were exported as separate output records, matching on the deterministic parameterTypeId (`{typeId}-{kebabCaseName}`) that createOutputParameters builds.
    const exportedOutputs = imported.outputs?.items || [];
    Object.entries(fieldDefinitions || {}).forEach(([fieldKey, field]) => {
      if (!isOutputFieldKey(fieldKey, field)) return;
      const parameterTypeId = outputParameterTypeId(fieldKey, typeId);
      const match = exportedOutputs.find((o) => o.parameterTypeId === parameterTypeId);
      if (match) {
        schemaFields[fieldKey] = { value: true, ...(match.unit ? { unit: match.unit } : {}) };
      }
    });

    if (template) setSelectedTemplate(template);
    setFormData(prev => ({
      ...prev,
      isEditMode: false,
      name: imported.name || "",
      description: imported.description || "",
      type: imported.type || template?.name || "",
      typeId,
      executionUrl: imported.executionUrl || "",
      schemaFields,
      dependency: imported.dependency || "",
      isActive: imported.isActive ?? true,
      ownerType: imported.organizationId ? "organization" : "personal",
      selectedOrgKey: imported.organizationId || "",
    }));
    // An external model's expected delivery comes across too; the new model starts grading when it is created
    setDelivery(deliveryFormFrom(parseDeliveryExpectation(imported.deliveryExpectation)));
    setCurrentStep(1);
  }, [modalState.properties?.importedSchema, modelSchemaTemplateState.templateList]);

  // Edit mode: resolve the (possibly late-loading) template for Step 2 WITHOUT resetting schemaFields.
  useEffect(() => {
    if (!formData.isEditMode || !formData.typeId || selectedTemplate) return;
    const { template } = resolveTemplate(formData.typeId);
    if (template) setSelectedTemplate(template);
  }, [formData.isEditMode, formData.typeId, selectedTemplate, modelSchemaTemplateState.templateList]);

  // --- Schedule list handlers (Step 4) ---
  const addSchedule = () =>
    setFormData(prev => ({ ...prev, schedules: [...prev.schedules, defaultScheduleConfig()] }));
  const removeSchedule = (i) =>
    setFormData(prev => ({ ...prev, schedules: prev.schedules.filter((_, idx) => idx !== i) }));
  const updateSchedule = (i) => (next) =>
    setFormData(prev => ({ ...prev, schedules: prev.schedules.map((c, idx) => (idx === i ? next : c)) }));

  const loadModelTemplates = async () => {
    try {
      setLoadingTypes(true);
      await dispatch(fetchModelSchemaTemplates());
    } catch (error) {
      console.error("Error loading model templates:", error);
    } finally {
      setLoadingTypes(false);
    }
  };

  // Convert template list to dropdown options (combining static and dynamic)
  const getTemplateOptions = () => {
    // Static schemas from modelSchemas.ts
    const staticOptions = Object.entries(MODEL_SCHEMAS).map(([id, schema]) => ({
      id: id,
      name: schema.name,
      description: schema.description,
      category: schema.category,
      version: schema.version,
      isStatic: true
    }));

    // Dynamic schemas from database
    const dynamicOptions = modelSchemaTemplateState.templateList.map(template => ({
      id: template.id,
      name: template.name,
      description: template.description,
      category: template.category,
      version: template.version,
      isStatic: false
    }));

    // Combine both, with dynamic templates first
    return [...dynamicOptions, ...staticOptions];
  };

  const handleTemplateChange = (templateId) => {
    // Check if it's a dynamic template from database
    let template = modelSchemaTemplateState.templateList.find(t => t.id === templateId);
    let fieldDefinitions = {};

    if (template) {
      // Dynamic template from database
      fieldDefinitions = template?.fieldDefinitions ? JSON.parse(template.fieldDefinitions) : {};
    } else {
      // Static template from modelSchemas.ts
      const staticSchema = MODEL_SCHEMAS[templateId];
      if (staticSchema) {
        template = {
          id: templateId,
          name: staticSchema.name,
          description: staticSchema.description,
          category: staticSchema.category,
          version: staticSchema.version,
          executionUrl: staticSchema.executionUrl,
        };
        fieldDefinitions = staticSchema.fields || {};
      }
    }

    setSelectedTemplate(template);

    // Update form data
    handleInputChange("typeId", templateId);
    handleInputChange("type", template?.name || "");
    handleInputChange("executionUrl", template?.executionUrl || "");

    // Initialize schema fields with default values
    if (fieldDefinitions && Object.keys(fieldDefinitions).length > 0) {
      handleInputChange("schemaFields", buildInitialSchemaFields(fieldDefinitions));

      // Clear any existing errors for schema fields
      const newErrors = { ...errors };
      Object.keys(fieldDefinitions).forEach(key => {
        delete newErrors[`schema_${key}`];
      });
      setErrors(newErrors);
    } else {
      handleInputChange("schemaFields", {});
    }
  };

  const handleSchemaFieldChange = (fieldKey, value) => {
    setFormData(prev => ({
      ...prev,
      schemaFields: {
        ...prev.schemaFields,
        [fieldKey]: value
      }
    }));

    // Clear error when user changes field
    if (errors[`schema_${fieldKey}`]) {
      setErrors(prev => ({
        ...prev,
        [`schema_${fieldKey}`]: null
      }));
    }
  };

  // A HISTORICAL template (stage 4): no Execution URL (the template's processor is used), a History step in place
  // of Data Sources, Triggers in place of Schedule, and the kind and history settings saved with the model
  const isHistorical = selectedTemplate?.modelKind === "HISTORICAL";
  // The template's merge policy decides the duplicate policy of the datasets its outputs write (quality rules replace
  // their window; a raw feed only adds)
  const templateMergePolicy: string | null = (() => {
    try {
      const raw = selectedTemplate?.historicalConfig;
      const config = typeof raw === "string" ? JSON.parse(raw) : raw;
      return config?.mergePolicy ?? null;
    } catch {
      return null;
    }
  })();
  // An EXTERNAL template (docs/architecture/EXTERNAL_FORECASTS.md): a FORECAST-kind model with no processor. Its
  // forecasts are uploaded, so it gets no Execution URL and never a ProcessedModelSchedule row — the Data Sources
  // step explains uploads and the Schedule step is a note. isHistorical stays false for it.
  const isExternal = selectedTemplate?.external === true;
  // The form starts with (and the edit pre-fill may load) a default schedule before the template has resolved —
  // in edit mode selectedTemplate arrives after the schedule rows do — so an external model's schedules are
  // cleared whenever they appear. There is no schedule editor for it, so nothing fights this.
  useEffect(() => {
    if (isExternal && (formData.schedules || []).length) {
      setFormData((prev) => ({ ...prev, schedules: [] }));
    }
  }, [isExternal, formData.schedules]);
  const templateRunConfig = useMemo(() => {
    let raw = selectedTemplate?.historicalConfig;
    try { raw = typeof raw === "string" ? JSON.parse(raw) : raw; } catch { raw = null; }
    return effectiveConfig(raw || null, null);
  }, [selectedTemplate]);
  const allowedTriggers = useMemo(() => allowedTriggersOf(templateRunConfig), [templateRunConfig]);
  // Step 4 has already checked it; null when switched off (clears a stored one on edit)
  const deliveryExpectationJson = () => {
    const value = expectationFromDeliveryForm(delivery, storedDelivery).value;
    return value ? JSON.stringify(value) : null;
  };
  // The two form fields over whatever else the model stored (a seeded overlap, say), which an edit must not drop
  const historySettings = () => historicalSettingsFromForm(editStoredHistorical, { processHistory: formData.processHistory, backfillFrom: formData.backfillFrom });
  const templateFieldDefinitions = useMemo(() => {
    try { return selectedTemplate?.fieldDefinitions ? JSON.parse(selectedTemplate.fieldDefinitions) : {}; } catch { return {}; }
  }, [selectedTemplate]);
  // Dataset and model_output fields: a change to either re-links and backfills by itself (no re-run prompt)
  const datasetFieldKeys = useMemo(
    () => Object.entries(templateFieldDefinitions).filter(([, f]) => f?.type === "dataset" || f?.type === "model_output").map(([k]) => k),
    [templateFieldDefinitions]
  );
  // What the model's history is read from: a dataset, or (a template with only model_output fields) a model output
  const historySource = useMemo(
    () => (Object.values(templateFieldDefinitions).some((f: any) => f?.type === "dataset") ? "dataset" : Object.values(templateFieldDefinitions).some((f: any) => f?.type === "model_output") ? "model output" : "none"),
    [templateFieldDefinitions]
  );
  // "2026-05-01 → today, 6 monthly runs", for the History step and the review
  const describeHistory = () =>
    describeHistoryPlan(
      { processHistory: formData.processHistory, backfillFrom: formData.backfillFrom },
      { now: toIso(Date.now()), config: templateRunConfig, editing: formData.isEditMode, source: historySource }
    );
  // Two stable selections merged once, so the open wizard doesn't re-render on every store action
  const personalDatasets = useSelector((state) => state.datasets?.personalDatasets);
  const organizationDatasets = useSelector((state) => state.datasets?.organizationDatasets);
  const datasetsInStore = useMemo(() => ({ ...(personalDatasets || {}), ...(organizationDatasets || {}) }), [personalDatasets, organizationDatasets]);
  const pickedDatasetNames = datasetFieldKeys
    .map((key) => datasetsInStore[formData.schemaFields[key]?.datasetId]?.name)
    .filter(Boolean);

  const steps = isHistorical
    ? [
        { id: 1, title: "Basic Information", description: "Name, description, and type" },
        { id: 2, title: "Parameters", description: "Dataset, columns and rules" },
        { id: 3, title: "History", description: "How much of the past to process" },
        { id: 4, title: "Triggers", description: "What starts a run" },
        { id: 5, title: "Review", description: "Review and create" }
      ]
    : isExternal
    ? [
        { id: 1, title: "Basic Information", description: "Name, description, and type" },
        { id: 2, title: "Parameters", description: "Configure model parameters" },
        { id: 3, title: "Uploads", description: "How forecasts get into this model" },
        { id: 4, title: "Schedule", description: "No schedule — forecasts are uploaded" },
        { id: 5, title: "Review", description: "Review and create" }
      ]
    : [
        { id: 1, title: "Basic Information", description: "Name, description, and type" },
        { id: 2, title: "Parameters", description: "Configure model parameters" },
        { id: 3, title: "Data Sources", description: "Upload files and configure data sources" },
        { id: 4, title: "Schedule", description: "Set run schedule (optional)" },
        { id: 5, title: "Review", description: "Review and create" }
      ];

  const handleClose = () => {
    dispatch(setModalOpen(false));
    if (navigateOnCreate) navigate(navigateOnCreate);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };


  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!formData.type.trim()) newErrors.type = "Type is required";
        // Execution URL is optional (commented out for future use) if (!formData.executionUrl.trim()) { newErrors.executionUrl = "Execution URL is required"; } executionUrl is optional, accepts any string Validate organization selection
        if (formData.ownerType === "organization") {
          if (!formData.selectedOrgKey) {
            newErrors.owner = "Please select an organization";
          } else if (!user?.organizations?.data?.[formData.selectedOrgKey]) {
            newErrors.owner = "Selected organization is not valid";
          }
        }
        break;
      case 2:
        // Validate schema fields
        if (selectedTemplate?.fieldDefinitions) {
          const fieldDefinitions = JSON.parse(selectedTemplate.fieldDefinitions);
          Object.entries(fieldDefinitions).forEach(([key, field]) => {
            const value = formData.schemaFields[key];
            const validation = validateFieldValue(value, field);
            if (!validation.isValid) {
              newErrors[`schema_${key}`] = validation.error;
            }
            // The rules form's checks (stage 4, decision 5): every box must hold what the processor reads
            if (field?.type === "dict" && field?.validation?.rulesFor) {
              const columns = formData.schemaFields[field.validation.rulesFor]?.columns || [];
              const problems = rulesErrors(value, Array.isArray(columns) ? columns : []);
              if (problems.length) newErrors[`schema_${key}`] = problems[0].message;
            }
          });

          // Require at least one output to be selected when the template defines outputs. Skip in edit mode: outputs are stored as separate parameter records and stripped from schemaFields, so none would appear selected.
          const outputKeys = formData.isEditMode ? [] : Object.keys(fieldDefinitions).filter(
            (key) => isOutputFieldKey(key, fieldDefinitions[key])
          );
          if (outputKeys.length > 0) {
            const anyOutputSelected = outputKeys.some((key) => {
              const value = formData.schemaFields[key];
              return value === true || (value && typeof value === 'object' && value.value === true);
            });
            if (!anyOutputSelected) {
              newErrors.outputs = "Select at least one output";
            }
          }
          // A historical output goes to a dataset: an existing one needs choosing, and not a dataset the model reads
          if (isHistorical) {
            outputKeys.forEach((key) => {
              const value = formData.schemaFields[key];
              const ticked = value === true || (value && typeof value === 'object' && value.value === true);
              const problem = ticked ? destinationProblem(value, formData.schemaFields) : null;
              if (problem) newErrors[`schema_${key}`] = problem;
            });
          }
        }
        break;
      case 3:
        // History (historical): a from-date must be a real day in the past
        if (isHistorical && formData.processHistory && formData.backfillFrom) {
          const start = Date.parse(`${formData.backfillFrom}T00:00:00Z`);
          if (Number.isNaN(start)) newErrors.backfillFrom = "Enter a date";
          else if (start >= Date.now()) newErrors.backfillFrom = "The date must be in the past";
        }
        break;
      case 4:
        // An external forecast model has no schedules, only an optional expected delivery; a historical template
        // without scheduled runs has none to check
        if (isExternal) {
          const checked = expectationFromDeliveryForm(delivery, storedDelivery);
          if (checked.error) newErrors.delivery = checked.error;
          break;
        }
        if (isHistorical && !allowedTriggers.includes("CRON")) break;
        // Each schedule must be complete for its mode. Zero schedules is allowed (manual-only).
        (formData.schedules || []).forEach((cfg, i) => {
          if (cfg.mode === "once" && !cfg.firstRunTime) {
            newErrors[`schedule_${i}`] = "Pick a first-run time for the one-off schedule";
          }
          if (cfg.mode === "custom" && !cfg.cronExpression?.trim()) {
            newErrors[`schedule_${i}`] = "Enter a cron expression for the custom schedule";
          }
        });
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      const nextStep = Math.min(currentStep + 1, steps.length);

      // Generate ID when moving to review step (Step 5) if not already generated
      if (nextStep === 5 && !schemaId) {
        const newId = generateProcessedModelSchemaId(formData.name, formData.type);
        setSchemaId(newId);
      }
      
      setCurrentStep(nextStep);
    }
  };

  const handlePrevious = () => {
    // If on step 1 and came from a template picker, go back to whichever one opened us. __backTo defaults to the legacy Model Garden modal so existing callers are unchanged.
    if (currentStep === 1 && modalState.properties?.selectedTemplate) {
      const backTo = modalState.properties?.__backTo;
      dispatch(setModalOpen(false));
      // null = opened from the Model Templates page, which is already the picker behind this modal
      if (backTo === null) return;
      // Re-open the originating picker after a brief delay
      setTimeout(() => {
        dispatch(setModalState({
          modalOpen: true,
          modalView: backTo?.modalView || modalViewsConst.modelGarden,
          properties: backTo?.properties || {}
        }));
      }, 100);
    } else {
      setCurrentStep(prev => Math.max(prev - 1, 1));
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    // Backstop for the tier cap — the entry points gate first, but the JSON-import path and a deploy made elsewhere since this wizard opened both land here. Edits never count against it.
    if (!formData.isEditMode) {
      const limitCheck = evaluateModelLimit();
      if (limitCheck && !limitCheck.allowed) {
        capture(POSTHOG_EVENTS.MODEL_LIMIT_REACHED, {
          tier: limitCheck.tier,
          limit: limitCheck.limit,
          current_count: limitCheck.currentCount,
          source: "model_wizard",
          item: "model",
        });
        setErrors({
          submit: limitCheck.requiresPaidPlan
            ? "Deploying models requires a paid plan. Subscribe to Essential or Pro from Settings → Subscriptions."
            : `Your plan includes up to ${limitCheck.limit} model${limitCheck.limit !== 1 ? "s" : ""} and you already have ${limitCheck.currentCount}. Upgrade your plan from Settings → Subscriptions to deploy more.`,
        });
        return;
      }
    }

    // Reset operations log and start submission
    setOperationsLog([]);
    setIsSubmitting(true);
    setSubmissionComplete(false);
    setErrors({});

    try {
      // Use pre-generated schemaId or generate a new one if missing (should be there from review step)
      const meaningfulUuid = schemaId || generateProcessedModelSchemaId(formData.name, formData.type);
      if (!schemaId) setSchemaId(meaningfulUuid);

      // Wire the wizard's Operations Log + upload-progress UI into the shared pipeline hooks.
      const hooks = {
        addOp: addOperation,
        updateOp: updateOperation,
        setStatus: setUploadStatus,
        onFileProgress: (fieldName, pct) => {
          setCurrentUploadFile(pct >= 100 ? '' : fieldName);
          setUploadProgress(prev => ({ ...prev, [fieldName]: pct }));
        },
      };

      if (formData.isEditMode) {
        // -------- EDIT: update schema + reconcile schedules. Type/outputs/assets are locked. --------
        const { organizationId, owner, s3BasePath } = buildProcessedModelDeployTarget({
          user: { ...user, isAdmin },
          ownerType: formData.ownerType,
          selectedOrgKey: formData.selectedOrgKey,
          schemaId: meaningfulUuid,
        });

        let filteredSchemaFields;
        try {
          setIsUploading(true);
          filteredSchemaFields = await prepareSchemaFieldsForSave({
            schemaFields: formData.schemaFields,
            template: selectedTemplate,
            typeId: formData.typeId,
            s3BasePath,
            hooks,
          });
        } catch (uploadError) {
          setErrors({ submit: `Failed to upload files: ${uploadError.message}` });
          setSubmissionComplete(true);
          return; // Stop here — don't update the record if files failed
        } finally {
          setIsUploading(false);
        }

        // The schema's required runSchedule is a representative of the first schedule; the authoritative schedules live in ProcessedModelSchedule rows (reconciled below).
        // An external forecast model never runs: whatever the form holds, it saves no execution URL and no schedule rows.
        const representativeRunSchedule = (!isExternal && formData.schedules && formData.schedules.length)
          ? scheduleConfigToRunSchedule(formData.schedules[0])
          : { firstRunTime: "", frequency: "ONCE", cronExpression: "", timezone: "UTC" };

        const recordOpId = `record-${meaningfulUuid}`;
        addOperation(recordOpId, 'record', `Model Schema: ${meaningfulUuid}`, 'in_progress');
        setUploadStatus('Updating model schema record...');
        const updateInput = {
          id: meaningfulUuid,
          name: formData.name,
          description: formData.description,
          executionUrl: isHistorical || isExternal ? "" : formData.executionUrl,
          schemaFields: JSON.stringify(filteredSchemaFields),
          runSchedule: JSON.stringify(representativeRunSchedule),
          isActive: formData.isActive,
          activeStatus: formData.isActive ? "ACTIVE" : "INACTIVE",
          dependency: formData.dependency || null,
          ...(isHistorical ? { historicalSettings: JSON.stringify(historySettings()) } : {}),
          // Sent only when tracking is on or was on: an untouched model never has the field rewritten
          ...(isExternal && (delivery.enabled || storedDelivery) ? { deliveryExpectation: deliveryExpectationJson() } : {}),
        };
        const editResult = await dispatch(
          editProcessedModelSchemaFunc({ input: updateInput })
        );
        if (!editProcessedModelSchemaFunc.fulfilled.match(editResult)) {
          updateOperation(recordOpId, { status: 'error', error: editResult.error?.message || 'Unknown error' });
          setErrors({ submit: `Failed to update model schema: ${editResult.error?.message || 'Unknown error'}` });
          setSubmissionComplete(true);
          return;
        }
        updateOperation(recordOpId, { status: 'success' });

        // Reconcile schedules (logs each create/update/delete into the Operations Log). An external forecast model
        // and a historical template without scheduled runs keep none.
        setUploadStatus('Updating schedules...');
        await reconcileProcessedModelSchedules(
          meaningfulUuid,
          isExternal || (isHistorical && !allowedTriggers.includes("CRON")) ? [] : formData.schedules,
          { isActive: formData.isActive, owner, organizationId },
          { addOp: addOperation, updateOp: updateOperation }
        );

        // A changed setting (other than the dataset) leaves the past computed the old way: offer to re-run it
        // (stage 4, decision 7). A dataset change backfills by itself (stage 3, decision 4).
        if (isHistorical) {
          const stored = modalState.properties?.schemaToEdit;
          let before = editBefore;
          if (!before) { try { before = JSON.parse(stored?.schemaFields || "{}"); } catch { before = {}; } }
          setRerunPrompt(
            rerunPromptFor(before, filteredSchemaFields, {
              datasetFieldKeys,
              dataStart: await editDataStartRef.current,
              backfillFrom: historySettings().backfillFrom ?? null,
              now: toIso(Date.now()),
              config: templateRunConfig,
            })
          );
        }

        const editRefreshOpId = `refresh-${meaningfulUuid}`;
        addOperation(editRefreshOpId, 'refresh', 'Refreshing schema data', 'in_progress');
        await dispatch(fetchProcessedModelSchemaById(meaningfulUuid));
        updateOperation(editRefreshOpId, { status: 'success' });

        setUploadStatus('✅ Model schema updated successfully!');
        setSubmissionComplete(true);
        return;
      }

      // -------- CREATE: run the shared deploy pipeline (upload → schema → outputs → links → schedules). --------
      try {
        setIsUploading(true);
        await deployProcessedModelSchema({
          dispatch,
          user: { ...user, isAdmin },
          template: selectedTemplate,
          form: {
            name: formData.name,
            description: formData.description,
            type: formData.type,
            typeId: formData.typeId,
            executionUrl: isHistorical || isExternal ? "" : formData.executionUrl,
            schemaFields: formData.schemaFields,
            // An external forecast model never runs: no schedule rows, whatever the form holds
            schedules: isExternal || (isHistorical && !allowedTriggers.includes("CRON")) ? [] : formData.schedules,
            isActive: formData.isActive,
            ownerType: formData.ownerType,
            selectedOrgKey: formData.selectedOrgKey,
            dependency: formData.dependency,
            ...(isHistorical ? { modelKind: "HISTORICAL", historicalSettings: historySettings() } : {}),
            ...(isExternal ? { deliveryExpectation: expectationFromDeliveryForm(delivery, null).value ?? null } : {}),
          },
          hooks,
          schemaId: meaningfulUuid,
        });
      } finally {
        setIsUploading(false);
      }

      setUploadStatus('✅ Model schema created successfully!');
      setCreateSucceeded(true);
      setSubmissionComplete(true);

    } catch (error) {
      console.error("Error creating schema:", error);
      // The deploy pipeline throws ProcessedModelDeployError with a complete, user-facing message (it already says whether the schema was created); only plain errors need the prefix.
      const message = error instanceof ProcessedModelDeployError
        ? error.message
        : `Failed to create model schema: ${error.message}`;
      setErrors({ submit: message });
      setUploadStatus('');
      setSubmissionComplete(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 4's Dependency select, shared by the FORECAST and external layouts. Nothing in the backend reads
  // `dependency` today; it is stored with the model as before.
  const renderDependencyField = () => (
    <div>
      <label className="block font-outfit text-xs text-eai-ink-muted mb-2">
        Dependency
      </label>
      <Select
        value={formData.dependency}
        onChange={(e) => handleInputChange("dependency", e.target.value)}
      >
        <option value="">None</option>
        <option value="CFS-10-month">CFS - 10 month</option>
        <option value="GEFS-35-day">GEFS - 35 Day</option>
      </Select>
      <p className="mt-1 eai-description-sm">
        Select a data dependency for the EventBridge pipeline trigger
      </p>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block font-outfit text-xs text-eai-ink-muted mb-2">
                Model Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`eai-input ${errors.name ? 'border-eai-danger-solid' : ''}`}
                placeholder="Enter model name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-eai-danger-text">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block font-outfit text-xs text-eai-ink-muted mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                rows={3}
                className="eai-input"
                placeholder="Describe what this model does"
              />
            </div>

            <div>
              <label htmlFor={modelTypeId} className="block font-outfit text-xs text-eai-ink-muted mb-2">
                Model Type *
              </label>
              {(modalState.properties?.selectedTemplate || modalState.properties?.importedSchema || formData.isEditMode) ? (
                // Pre-selected from Model Garden, imported from JSON, or editing an existing model — read-only/locked.
                <div className="p-3 bg-eai-surface-sunken border border-eai-border rounded-eai-card">
                  <p className="text-sm text-eai-ink-soft">
                    <strong>{selectedTemplate?.name || formData.type}</strong>
                    {selectedTemplate?.version ? ` (v${selectedTemplate.version})` : ""}
                  </p>
                  <p className="text-sm text-eai-ink-muted italic mt-1">
                    {selectedTemplate?.description}
                  </p>
                  <p className="text-xs text-eai-ink-subtle mt-1">
                    Category: {selectedTemplate?.category}
                  </p>
                  {formData.isEditMode && (
                    <p className="text-xs text-eai-ink-faint mt-1">Model type is locked while editing.</p>
                  )}
                </div>
              ) : (
                // Normal dropdown when not pre-selected
                <>
                  <Select
                    id={modelTypeId}
                    searchable
                    disabled={loadingTypes}
                    placeholder={loadingTypes ? "Loading options…" : "Select a model type"}
                    value={formData.typeId}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    options={getTemplateOptions().map((o) => ({ value: o.id, label: o.name, description: o.description, group: o.category || "Other" }))}
                    error={errors.type}
                  />
                  {selectedTemplate && (
                    <div className="mt-2 p-3 bg-eai-surface-sunken border border-eai-border rounded-eai-card">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-eai-info-text">
                          <strong>{selectedTemplate.name}</strong> (v{selectedTemplate.version})
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-eai-card ${
                          selectedTemplate.fieldDefinitions
                            ? 'bg-eai-success-surface text-eai-success-text'
                            : 'bg-eai-surface-hover text-eai-ink-soft'
                        }`}>
                          {selectedTemplate.fieldDefinitions ? 'Database' : 'Static'}
                        </span>
                      </div>
                      <p className="text-sm text-eai-info-text italic mt-1">
                        {selectedTemplate.description}
                      </p>
                      <p className="text-xs text-eai-info-text mt-1">
                        Category: {selectedTemplate.category}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {isExternal && (
              <div className="p-3 bg-eai-surface-sunken border border-eai-border rounded-eai-card" data-testid="external-model-info">
                <p className="text-sm font-medium text-eai-ink">External forecast model</p>
                <p className="text-sm text-eai-ink-muted mt-1">
                  This model has no processor. Its forecasts are uploaded from the model card or with the API
                  (<code className="text-xs">POST /api/processed-output/:outputId/uploads</code>) and appear everywhere
                  a model-made forecast does.
                </p>
              </div>
            )}

            {!isHistorical && !isExternal && (
            <div>
              <label className="block font-outfit text-xs text-eai-ink-muted mb-2">
                Execution URL <span className="text-xs text-eai-ink-subtle">(optional - for future use)</span>
              </label>
              <input
                type="url"
                value={formData.executionUrl}
                onChange={(e) => handleInputChange("executionUrl", e.target.value)}
                className={`eai-input ${errors.executionUrl ? 'border-eai-danger-solid' : ''}`}
                placeholder="https://your-model-service.run.app/execute"
                disabled={formData.typeId && formData.typeId !== "custom-v1"}
              />
              {errors.executionUrl && (
                <p className="mt-1 text-sm text-eai-danger-text">{errors.executionUrl}</p>
              )}
              <p className="mt-1 eai-description-sm">
                Cloud Run or Lambda endpoint URL (not currently used)
              </p>
            </div>
            )}

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange("isActive", e.target.checked)}
                  className="h-4 w-4 text-eai-cta-primary rounded-eai-card border-eai-border focus:ring-eai-cta-primary"
                />
                <span className="text-sm font-medium text-eai-ink-soft">Active</span>
              </label>
              <p className="mt-1 eai-description-sm">
                When inactive, the model's schedules are paused and the poller skips it.
              </p>
            </div>

            <div>
              <label className="block font-outfit text-xs text-eai-ink-muted mb-2">
                Owner *
              </label>
              <Select
                value={formData.ownerType === "personal" ? "personal" : formData.selectedOrgKey}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "personal") {
                    handleInputChange("ownerType", "personal");
                    handleInputChange("selectedOrgKey", "");
                  } else {
                    handleInputChange("ownerType", "organization");
                    handleInputChange("selectedOrgKey", value);
                  }
                }}
                className={errors.owner ? "border-eai-danger focus:border-eai-danger focus:ring-eai-danger" : ""}
              >
                <option value="personal">Personal ({user?.permissionDetails?.name || 'You'})</option>
                {user?.organizations?.data && Object.entries(user.organizations.data).map(([orgKey, orgData]) => (
                  <option key={orgKey} value={orgKey}>
                    {orgData.name}
                  </option>
                ))}
              </Select>
              {errors.owner && (
                <p className="mt-1 text-sm text-eai-danger-text">{errors.owner}</p>
              )}
              <p className="mt-1 eai-description-sm">
                Choose who owns this model schema. Organizations allow team access to the schema and files.
              </p>
              {formData.ownerType === "organization" && formData.selectedOrgKey && (
                <div className="mt-2 p-3 bg-eai-surface-sunken border border-eai-border rounded-eai-card">
                  <p className="text-sm text-eai-info-text">
                    <strong>Organization:</strong> {user?.organizations?.data?.[formData.selectedOrgKey]?.name}
                  </p>
                  <p className="text-xs text-eai-info-text">
                    Files will be stored in organization storage and accessible to team members.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-eai-surface-sunken p-4 rounded-eai-card">
              <p className="text-sm text-eai-ink-muted">
                <strong>Schema ID:</strong> {schemaId || 'Generated on submit'}
              </p>
              <p className="text-xs text-eai-ink-subtle mt-1">
                A meaningful UUID will be generated when you submit: name-type-uuid
              </p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            {!selectedTemplate || !formData.typeId ? (
              <div className="bg-eai-surface-sunken border border-eai-border p-4 rounded-eai-card">
                <p className="text-sm text-eai-warning-text">
                  Please select a model type in Step 1 to configure parameters.
                </p>
              </div>
            ) : (() => {
              // Handle both static schemas (already parsed) and dynamic schemas (JSON)
              let fieldDefinitions = {};
              if (selectedTemplate.fieldDefinitions) {
                // Dynamic schema from database - needs parsing
                try {
                  fieldDefinitions = JSON.parse(selectedTemplate.fieldDefinitions);
                } catch (e) {
                  console.error("Error parsing field definitions:", e);
                  fieldDefinitions = {};
                }
              } else {
                // Static schema from MODEL_SCHEMAS - already parsed
                const staticSchema = MODEL_SCHEMAS[formData.typeId];
                fieldDefinitions = staticSchema?.fields || {};
              }

              return (
                <div className="space-y-4">
                  <div className="bg-eai-surface p-4 rounded-eai-card">
                    <h4 className="text-sm font-medium text-eai-ink-soft mb-2">
                      {selectedTemplate.name} Parameters
                    </h4>
                    <p className="text-sm text-eai-ink-muted">
                      Fill in the required information for your {selectedTemplate.name.toLowerCase()} model.
                    </p>
                  </div>

                  <div className="space-y-6">
                    {(() => {
                      // Group fields by section
                      const fieldsBySection = Object.entries(fieldDefinitions).reduce((acc, [key, field]) => {
                        const section = field.section || 'general';
                        if (!acc[section]) acc[section] = [];
                        acc[section].push([key, field]);
                        return acc;
                      }, {});

                      // Honor the admin-defined field order within each section. Fields without an explicit order keep their insertion order.
                      Object.keys(fieldsBySection).forEach((section) => {
                        fieldsBySection[section] = fieldsBySection[section]
                          .map((entry, i) => ({ entry, i }))
                          .sort((a, b) => {
                            const oa = a.entry[1].order ?? a.i;
                            const ob = b.entry[1].order ?? b.i;
                            return oa - ob;
                          })
                          .map((x) => x.entry);
                      });

                      // Define section order and titles
                      const sectionConfig = {
                        'configuration': 'Configuration',
                        'inputs': 'Data Inputs',
                        'outputs': 'Outputs',
                        'output': 'Outputs',
                        'general': 'General'
                      };

                      return Object.entries(sectionConfig).map(([sectionKey, sectionTitle]) => {
                        const sectionFields = fieldsBySection[sectionKey];
                        if (!sectionFields || sectionFields.length === 0) return null;

                        return (
                          <div key={sectionKey} className="space-y-4">
                            <div className="border-b border-eai-border pb-2">
                              <h3 className="text-md font-medium text-eai-ink">{sectionTitle}</h3>
                              {(sectionKey === 'outputs' || sectionKey === 'output') && (
                                <p className="text-sm text-eai-ink-subtle mt-1">
                                  Select which parameters to include in the model output
                                </p>
                              )}
                              {(sectionKey === 'outputs' || sectionKey === 'output') && errors.outputs && (
                                <p className="text-sm text-eai-danger-text-soft mt-1">{errors.outputs}</p>
                              )}
                            </div>

                            <div className={`grid gap-4 ${sectionKey === 'outputs' ? 'grid-cols-1 space-y-2' : 'grid-cols-1'}`}>
                              {sectionFields.map(([key, field]) => (
                                <SchemaFieldRenderer
                                  key={key}
                                  fieldKey={key}
                                  field={field}
                                  value={formData.schemaFields[key]}
                                  onChange={(value) => handleSchemaFieldChange(key, value)}
                                  error={errors[`schema_${key}`]}
                                  siblingValues={formData.schemaFields}
                                  context={{ ownerType: formData.ownerType, selectedOrgKey: formData.selectedOrgKey, isAdmin, editingModelId: formData.isEditMode ? schemaId || modalState.properties?.schemaToEdit?.id : undefined, modelKind: selectedTemplate?.modelKind ?? null, mergePolicy: templateMergePolicy }}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      }).filter(Boolean);
                    })()}
                  </div>
                </div>
              );
            })()}
          </div>
        );

      case 3:
        if (isHistorical) return (
          <div className="space-y-6" data-testid="history-step">
            <div>
              <h3 className="eai-h3-title mb-4">History</h3>
              <p className="text-sm text-eai-ink-muted">
                {historySource === "none"
                  ? formData.isEditMode
                    ? "Changing the date below doesn't queue a backfill on its own: use Re-run a range on the model to fill the new span."
                    : "A historical model backfills from the date below as soon as it is created, then its schedule keeps up from where the backfill ended."
                  : `A historical model processes its ${historySource}'s past once, then keeps up as new data arrives.`}
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.processHistory}
                onChange={(e) => handleInputChange("processHistory", e.target.checked)}
                className="h-4 w-4 text-eai-cta-primary rounded-eai-card border-eai-border focus:ring-eai-cta-primary"
                data-testid="process-history"
              />
              <span className="text-sm text-eai-ink">Process history</span>
            </label>
            {formData.processHistory && (
              <div>
                <label className="block font-outfit text-xs text-eai-ink-muted mb-2">From (UTC day, optional)</label>
                <input
                  type="date"
                  value={formData.backfillFrom}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => handleInputChange("backfillFrom", e.target.value)}
                  className={`eai-input ${errors.backfillFrom ? 'border-eai-danger-solid' : ''}`}
                  data-testid="backfill-from"
                />
                {errors.backfillFrom && <p className="mt-1 text-sm text-eai-danger-text">{errors.backfillFrom}</p>}
              </div>
            )}
            <p className="eai-description-sm" data-testid="history-preview">{describeHistory()}</p>
          </div>
        );
        if (isExternal) return (
          <div className="space-y-6" data-testid="uploads-step">
            <div>
              <h3 className="eai-h3-title mb-4">Uploads</h3>
              <p className="text-sm text-eai-ink-muted">
                This model has no processor, so there is nothing to configure here. Each forecast is a file uploaded
                after the model is created.
              </p>
            </div>

            <div className="bg-eai-surface-sunken border border-eai-border rounded-eai-card p-4 space-y-4">
              <div>
                <h4 className="text-sm font-medium text-eai-ink">Who can upload</h4>
                <p className="text-sm text-eai-ink-muted mt-1">The model's owner, members of its organization, and Admins.</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-eai-ink">How</h4>
                <p className="text-sm text-eai-ink-muted mt-1">From the model card, or with the API:</p>
                <code className="text-xs bg-eai-surface-hover p-2 rounded-eai-card block mt-2">
                  POST /api/processed-output/:outputId/uploads
                </code>
              </div>
              <div>
                <h4 className="text-sm font-medium text-eai-ink">What</h4>
                <ul className="list-disc pl-5 text-sm text-eai-ink-muted space-y-1 mt-1">
                  <li>A Standard time-series CSV, one file per forecast issue.</li>
                  <li>
                    The issue time comes from the request, the file's <code className="text-xs">forecast_issued</code> row,
                    or a date in the file name.
                  </li>
                  <li>Uploaded forecasts appear everywhere a model-made forecast does.</li>
                </ul>
              </div>
            </div>
          </div>
        );
        return (
          <div className="space-y-6">
            <div>
              <h3 className="eai-h3-title mb-4">Data Sources</h3>
              <p className="text-sm text-eai-ink-muted mb-4">
                Upload files or configure data sources for your model. Files will be stored at:
              </p>
              <code className="text-xs bg-eai-surface-hover p-2 rounded-eai-card block mb-4">
                {getDisplayPath(schemaId)}
              </code>
            </div>

            <div className="border-2 border-dashed border-eai-border rounded-eai-card p-8 text-center">
              <div className="space-y-2">
                <p className="text-eai-ink-subtle">File upload component will be implemented here</p>
                <p className="text-sm text-eai-ink-faint">
                  Supported formats: CSV, JSON, NetCDF, and more
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-md font-medium text-eai-ink">Current Data Sources:</h4>
              {formData.dataSources.length === 0 ? (
                <p className="text-sm text-eai-ink-subtle italic">No data sources configured yet</p>
              ) : (
                <div className="space-y-2">
                  {formData.dataSources.map((source, index) => (
                    <div key={index} className="bg-eai-surface-sunken p-3 rounded-eai-card border">
                      <p className="font-medium">{source.name}</p>
                      <p className="text-sm text-eai-ink-muted">{source.sourceType}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 4:
        if (isHistorical) return (
          <div className="space-y-6" data-testid="triggers-step">
            <div>
              <h3 className="eai-h3-title mb-4">Triggers</h3>
              <ul className="list-disc pl-5 text-sm text-eai-ink-muted space-y-1">
                {allowedTriggers.includes("DATASET_UPLOAD") && (
                  <li>Runs automatically when {pickedDatasetNames.length ? pickedDatasetNames.join(", ") : "its dataset"} gets new data.</li>
                )}
                {allowedTriggers.includes("MODEL_OUTPUT") && <li>Runs automatically when a linked model output gets new data.</li>}
                {(allowedTriggers.includes("MANUAL") || allowedTriggers.includes("BACKFILL")) && (
                  <li>
                    {[allowedTriggers.includes("MANUAL") && "Run now", allowedTriggers.includes("BACKFILL") && "Re-run a range"].filter(Boolean).join(" and ")} on the
                    model's page, for its owner and Admins.
                  </li>
                )}
                {!allowedTriggers.includes("CRON") && <li>No scheduled runs: this template runs on data, not on a clock.</li>}
              </ul>
            </div>
            {allowedTriggers.includes("CRON") && (
              <div className="space-y-4">
                <h4 className="text-md font-medium text-eai-ink">Schedules (optional)</h4>
                {(formData.schedules || []).map((cfg, i) => (
                  <div key={cfg.id || i}>
                    <ScheduleEditor
                      index={i}
                      value={cfg}
                      onChange={updateSchedule(i)}
                      onRemove={formData.schedules.length > 1 ? () => removeSchedule(i) : undefined}
                    />
                    {errors[`schedule_${i}`] && (
                      <p className="mt-1 text-sm text-eai-danger-text">{errors[`schedule_${i}`]}</p>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addSchedule} className="flex items-center gap-1 font-outfit text-sm text-eai-cta-primary hover:underline">
                  <FiPlus size={14} /> Add another schedule
                </button>
              </div>
            )}
          </div>
        );
        if (isExternal) return (
          <div className="space-y-6" data-testid="external-schedule-step">
            <div>
              <h3 className="eai-h3-title mb-4">Schedule</h3>
              <div className="bg-eai-surface-sunken border border-eai-border rounded-eai-card p-4">
                <p className="text-sm text-eai-ink">
                  No schedule: an external forecast model never runs. New forecasts arrive when they are uploaded.
                </p>
              </div>
            </div>
            <div className="space-y-3" data-testid="expected-delivery">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={delivery.enabled}
                  onChange={(e) => setDelivery((d) => ({ ...d, enabled: e.target.checked }))}
                  data-testid="expected-delivery-toggle"
                />
                <span>
                  <span className="block text-md font-medium text-eai-ink">Track deliveries (optional)</span>
                  <span className="block text-sm text-eai-ink-muted">
                    Say when forecasts are issued and how long after each issue time its file should arrive. The
                    Uploads tab's calendar then grades every expected issue as on time, late, missed or rejected.
                  </span>
                </span>
              </label>
              {delivery.enabled && (
                <div className="space-y-3 pl-6">
                  <div>
                    <p className="mb-2 text-sm font-medium text-eai-ink">Forecasts are issued</p>
                    <ScheduleEditor
                      value={delivery.schedule}
                      onChange={(next) => setDelivery((d) => ({ ...d, schedule: next }))}
                      modes={["recurring", "custom"]}
                      nextLabel="Next issue"
                    />
                  </div>
                  <div>
                    <label htmlFor="delivery-allowance" className="mb-1 block text-sm font-medium text-eai-ink">
                      Each forecast should arrive within
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        id="delivery-allowance"
                        type="number"
                        min={0}
                        step="any"
                        value={delivery.allowanceValue}
                        onChange={(e) => setDelivery((d) => ({ ...d, allowanceValue: e.target.value }))}
                        className="eai-input w-24"
                        data-testid="delivery-allowance"
                      />
                      <Select
                        aria-label="Allowance unit"
                        containerClassName="w-28"
                        value={delivery.allowanceUnit}
                        onChange={(e) => setDelivery((d) => ({ ...d, allowanceUnit: e.target.value === "h" ? "h" : "min" }))}
                        options={[
                          { value: "min", label: "minutes" },
                          { value: "h", label: "hours" },
                        ]}
                        data-testid="delivery-allowance-unit"
                      />
                      <span className="text-sm text-eai-ink-muted">of its issue time</span>
                    </div>
                    <p className="mt-1 text-xs text-eai-ink-subtle">
                      Matched by the issue time each upload is filed under. Changing when forecasts are issued restarts
                      the grading from that moment.
                    </p>
                  </div>
                </div>
              )}
              {errors.delivery && <p className="text-sm text-eai-danger-text" data-testid="delivery-error">{errors.delivery}</p>}
            </div>
          </div>
        );
        return (
          <div className="space-y-6">
            <div>
              <h3 className="eai-h3-title mb-4">Run Schedules (Optional)</h3>
              <p className="text-sm text-eai-ink-muted mb-4">
                Add one or more schedules for when this model should run. Leave none for a
                manual-only model.
              </p>
            </div>

            <div className="space-y-4">
              {(formData.schedules || []).map((cfg, i) => (
                <div key={cfg.id || i}>
                  <ScheduleEditor
                    index={i}
                    value={cfg}
                    onChange={updateSchedule(i)}
                    onRemove={formData.schedules.length > 1 ? () => removeSchedule(i) : undefined}
                  />
                  {errors[`schedule_${i}`] && (
                    <p className="mt-1 text-sm text-eai-danger-text">{errors[`schedule_${i}`]}</p>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addSchedule}
                className="flex items-center gap-1 font-outfit text-sm text-eai-cta-primary hover:underline"
              >
                <FiPlus size={14} /> Add another schedule
              </button>
            </div>

            {renderDependencyField()}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="eai-h3-title mb-4">Review and Create</h3>
              <p className="text-sm text-eai-ink-muted mb-6">
                Please review your model schema configuration before creating.
              </p>
            </div>

            <div className="bg-eai-surface-sunken p-4 rounded-eai-card space-y-4">
              <div>
                <h4 className="font-medium text-eai-ink">Basic Information</h4>
                <div className="mt-2 text-sm space-y-1">
                  <p><span className="font-medium">Name:</span> {formData.name}</p>
                  <p><span className="font-medium">Type:</span> {formData.type}</p>
                  <p><span className="font-medium">Description:</span> {formData.description || "None"}</p>
                  {!isHistorical && !isExternal && <p><span className="font-medium">Execution URL:</span> {formData.executionUrl || "None (optional)"}</p>}
                  {isHistorical && <p><span className="font-medium">Kind:</span> Historical (processor {selectedTemplate?.executionUrl || "from the template"})</p>}
                  {isExternal && <p data-testid="review-kind-external"><span className="font-medium">Kind:</span> External forecast (uploaded)</p>}
                  <p><span className="font-medium">Schema ID:</span> {schemaId || 'Generated on submit'}</p>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-eai-ink">Parameters</h4>
                <div className="mt-2 text-xs bg-eai-surface-card p-2 rounded-eai-card border max-h-96 overflow-y-auto">
                  {(() => {
                    // Calculate preview S3 path using the generated schemaId
                    const previewS3BasePath = getDisplayPath(schemaId);
                    const typedSchemaFields = injectTypesToSchemaFields(formData.schemaFields, selectedTemplate, formData.typeId, previewS3BasePath);
                    
                    return (
                      <pre className="whitespace-pre-wrap font-mono text-eai-ink-muted overflow-x-auto">
                        {JSON.stringify(typedSchemaFields, null, 2)}
                      </pre>
                    );
                  })()}
                </div>
              </div>

              {isHistorical ? (
                <div>
                  <h4 className="font-medium text-eai-ink">History</h4>
                  <p className="mt-2 text-sm" data-testid="review-history">{describeHistory()}</p>
                </div>
              ) : isExternal ? (
                <div>
                  <h4 className="font-medium text-eai-ink">Uploads</h4>
                  <p className="mt-2 text-sm" data-testid="review-uploads">
                    Forecasts are uploaded from the model card or the API once the model exists; its owner, organization members and Admins can upload.
                  </p>
                </div>
              ) : (
              <div>
                <h4 className="font-medium text-eai-ink">Data Sources</h4>
                <p className="mt-2 text-sm">
                  {formData.dataSources.length} data source(s) configured
                </p>
              </div>
              )}

              <div>
                <h4 className="font-medium text-eai-ink">Schedules</h4>
                {isExternal ? (
                  <>
                    <p className="mt-2 text-sm text-eai-ink-subtle" data-testid="review-no-schedule">
                      No schedule: an external forecast model never runs.
                    </p>
                    <p className="mt-1 text-sm" data-testid="review-expected-delivery">
                      <span className="font-medium">Expected delivery:</span> {summarizeDeliveryForm(delivery)}
                    </p>
                  </>
                ) : (
                <div className="mt-2 text-sm space-y-1">
                  {(formData.schedules || []).length === 0 && (
                    <p className="text-eai-ink-subtle">No schedules — manual runs only.</p>
                  )}
                  {(formData.schedules || []).map((cfg, i) => {
                    let label;
                    if (cfg.mode === "once") {
                      label = `Run once at ${formatDatetimeLocal(cfg.firstRunTime)}`;
                    } else if (cfg.mode === "custom") {
                      label = `Custom cron: ${cfg.cronExpression || "—"}`;
                    } else {
                      label = buildRuntime(cfg.freqType, cfg.hour, cfg.minute, cfg.ampm, cfg.weekDays.join(","), cfg.monthDay);
                    }
                    return (
                      <p key={cfg.id || i}>
                        {i + 1}. {label} <span className="text-eai-ink-faint">({cfg.timezone})</span>
                      </p>
                    );
                  })}
                </div>
                )}
              </div>

              <div>
                <h4 className="font-medium text-eai-ink">Storage Path</h4>
                <code className="mt-2 text-xs bg-eai-surface-card p-2 rounded-eai-card border block">
                  {getDisplayPath(schemaId)}
                </code>
              </div>
            </div>

            {/* Operations Log - shows during and after submission */}
            {operationsLog.length > 0 && (
              <div className="bg-eai-surface-sunken border border-eai-border rounded-eai-card p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-eai-ink">Operations Log</h4>
                  {isSubmitting && (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-eai-surface-sunken text-eai-info-text rounded-eai-card">
                      <svg className="animate-spin -ml-0.5 mr-1.5 h-3 w-3 text-eai-info-text" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  )}
                  {submissionComplete && !errors.submit && (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-eai-success-surface text-eai-success-text rounded-eai-card">
                      Complete
                    </span>
                  )}
                </div>

                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {operationsLog.map((op) => (
                    <div
                      key={op.id}
                      className={`flex items-start gap-3 p-2 rounded-eai-card text-sm ${
                        op.status === 'error' ? 'bg-eai-danger-surface' :
                        op.status === 'success' ? 'bg-eai-success-surface-soft' :
                        op.status === 'in_progress' ? 'bg-eai-surface-sunken' :
                        'bg-eai-surface-hover'
                      }`}
                    >
                      {/* Status icon */}
                      <span className="flex-shrink-0 mt-0.5">
                        {op.status === 'success' && (
                          <svg className="h-4 w-4 text-eai-success-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                        {op.status === 'error' && (
                          <svg className="h-4 w-4 text-eai-danger-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                        {op.status === 'in_progress' && (
                          <svg className="animate-spin h-4 w-4 text-eai-info-text" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        )}
                        {op.status === 'pending' && (
                          <svg className="h-4 w-4 text-eai-ink-faint" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" strokeWidth={2} />
                          </svg>
                        )}
                      </span>

                      {/* Operation details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="eai-tag-pill-sm shrink-0">
                            {op.type === 'record' ? 'Record' :
                             op.type === 'file' ? 'Upload' :
                             op.type === 'output' ? 'Output' :
                             op.type === 'asset_link' ? 'Link' :
                             op.type === 'schedule' ? 'Schedule' :
                             op.type === 'refresh' ? 'Refresh' :
                             op.type}
                          </span>
                          <span className={`truncate ${
                            op.status === 'error' ? 'text-eai-danger-text' :
                            op.status === 'success' ? 'text-eai-success-text' :
                            op.status === 'in_progress' ? 'text-eai-info-text' :
                            'text-eai-ink-muted'
                          }`}>
                            {op.name}
                          </span>
                        </div>
                        {op.error && (
                          <p className="mt-1 text-xs text-eai-danger-text truncate">{op.error}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Summary stats */}
                {submissionComplete && (
                  <div className="mt-3 pt-3 border-t border-eai-border flex gap-4 text-xs">
                    <span className="text-eai-success-text">
                      {operationsLog.filter(op => op.status === 'success').length} succeeded
                    </span>
                    {operationsLog.filter(op => op.status === 'error').length > 0 && (
                      <span className="text-eai-danger-text">
                        {operationsLog.filter(op => op.status === 'error').length} failed
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Current status message */}
            {uploadStatus && !submissionComplete && (
              <div className="bg-eai-surface-sunken border border-eai-border rounded-eai-card p-3">
                <p className="text-sm text-eai-info-text font-medium">{uploadStatus}</p>

                {/* File upload progress bar */}
                {isUploading && currentUploadFile && (
                  <div className="mt-2 space-y-1">
                    <div className="flex justify-between items-center text-xs text-eai-info-text">
                      <span>Uploading: {currentUploadFile}</span>
                      <span>{uploadProgress[currentUploadFile] || 0}%</span>
                    </div>
                    <div className="w-full bg-eai-surface-active rounded-full h-2">
                      <div
                        className="bg-eai-cta-primary h-2 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${uploadProgress[currentUploadFile] || 0}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {submissionComplete && !errors.submit && rerunPrompt && (
              <div className="bg-eai-surface-sunken border border-eai-border p-4 rounded-eai-card space-y-3" data-testid="rerun-prompt">
                <p className="text-sm text-eai-ink">{rerunPromptText(rerunPrompt, templateRunConfig)}</p>
                {rerunState === "queued" ? (
                  <p className="text-sm text-eai-success-text">Backfill queued. Watch it on the model's Runs tab.</p>
                ) : rerunState === "declined" ? (
                  <p className="text-sm text-eai-ink-subtle">Not re-run. Re-run a range on the model when you want to.</p>
                ) : rerunPrompt.problem ? null : (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={rerunState === "queuing"}
                      onClick={async () => {
                        setRerunState("queuing");
                        try {
                          await createRunRequest({ processedModelSchemaId: schemaId, triggerType: "BACKFILL", windowStart: rerunPrompt.window.start, windowEnd: rerunPrompt.window.end });
                          capture(POSTHOG_EVENTS.HISTORICAL_MODEL_RUN_REQUESTED, { model_id: schemaId, trigger: "BACKFILL", source: "edit_prompt" });
                          setRerunState("queued");
                        } catch (e) {
                          setRerunState("failed");
                          setErrors((prev) => ({ ...prev, rerun: e?.message || "Could not queue the re-run." }));
                        }
                      }}
                      className="eai-btn-cta disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid="rerun-yes"
                    >
                      {rerunState === "queuing" ? "Queuing…" : "Re-run history"}
                    </button>
                    <button type="button" onClick={() => setRerunState("declined")} className="eai-btn-outline" data-testid="rerun-no">
                      Not now
                    </button>
                  </div>
                )}
                {errors.rerun && <p className="text-sm text-eai-danger-text">{errors.rerun}</p>}
              </div>
            )}

            {errors.submit && (
              <div className="bg-eai-danger-surface border border-eai-danger-border rounded-eai-card p-3">
                <p className="text-sm text-eai-danger-text font-medium">Error</p>
                <p className="text-sm text-eai-danger-text mt-1">{errors.submit}</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Owner-or-Admin only; the opener decides via canDelete, like Edit Dataset
  const canDelete = formData.isEditMode && Boolean(modalState.properties?.canDelete);
  // Four footer pills don't fit a phone row, so with Delete they fall into an even 2×2 grid there
  const showDelete = canDelete && !submissionComplete;
  const handleDelete = () => {
    const schema = modalState.properties?.schemaToEdit;
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.generalDelete,
        properties: {
          __modalSize: "sm",
          title: "Delete Model",
          itemName: schema?.name,
          itemId: schema?.id,
          itemType: "processedModelSchema",
        },
      })
    );
  };

  const wizardTitle = formData.isEditMode
    ? "Edit Model Schema"
    : modalState.properties?.importedSchema
    ? "Create Model Schema (Imported)"
    : "Create Model Schema";

  return (
    <ModalBase
      title={wizardTitle}
      icon={<LightningIcon className="h-6 w-6 [&_path]:stroke-current text-eai-hero-accent" />}
      onClose={isSubmitting ? () => {} : handleClose}
      size="md"
      fillHeight
      footer={
        // ONE child on purpose: full-screen ModalBase applies [&>*]:flex-1, which split this three ways and wrapped "Save Changes" inside its own pill. As a single child the row keeps natural widths and wraps between buttons instead.
        <div
          className={`w-full gap-3 ${
            showDelete ? "grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center" : "flex flex-wrap items-center"
          }`}
        >
          {/* mr-auto, not justify-between: ModalBase's footer is justify-end, and Previous is the one control that belongs at the leading edge */}
          <button
            type="button"
            onClick={handlePrevious}
            disabled={(currentStep === 1 && !modalState.properties?.selectedTemplate) || isSubmitting}
            className={`eai-btn-outline shrink-0 ${showDelete ? "sm:mr-auto" : "mr-auto"} disabled:cursor-not-allowed disabled:opacity-50`}
          >
            <ChevronRightIcon className="h-4 w-4 rotate-180" />
            Previous
          </button>

          {showDelete && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={isSubmitting}
              className="eai-btn-danger disabled:cursor-not-allowed disabled:opacity-50"
            >
              Delete Model
            </button>
          )}

          {!submissionComplete && (
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="eai-btn-outline disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
          )}

          {currentStep < steps.length ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={isSubmitting}
              className="eai-btn-cta disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          ) : submissionComplete ? (
            <button type="button" onClick={handleClose} className="eai-btn-cta">
              {navigateOnCreate ? "View model" : "Close"}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={processedModelSchemaState.loading === "pending" || isUploading || isSubmitting}
              className="eai-btn-cta disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "Processing…" : formData.isEditMode ? "Save Changes" : "Create Model Schema"}
            </button>
          )}
        </div>
      }
    >
      {/* Step rail leads the body — ModalBase pins the header and footer, so this scrolls with the step it describes rather than needing a strip of its own chrome */}
      <div className="mb-5 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-outfit text-eai-control-sm font-medium ${
                  currentStep >= step.id
                    ? "bg-eai-cta-primary text-white"
                    : "bg-eai-surface-active text-eai-ink-muted"
                }`}
              >
                {step.id}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`h-1 flex-1 rounded-full ${
                    currentStep > step.id ? "bg-eai-cta-primary" : "bg-eai-surface-active"
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <p className="eai-description-sm">
          Step {currentStep} of {steps.length}: {steps[currentStep - 1]?.title}
        </p>
      </div>

      {renderStepContent()}
    </ModalBase>
  );
};

export default DynamicProcessedModelSchemaModal;